import { Rum } from '@porsche-code/kado-rum-client';
import {
  Breadcrumbs,
  ContentWrapper,
  Divider,
  Main,
  Page,
  PageNotAvailable,
} from '@porsche-code/kado-ui';
import {
  AnyRouteMatch,
  createRootRouteWithContext,
  useLocation,
} from '@tanstack/react-router';
import { Suspense, lazy, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Footer, Header, SignIn } from '@/components';
import { COMMON_NAMESPACE, NAMESPACES } from '@/config/i18n';
import { usePersonContext } from '@/context';

const TanStackRouterDevtools = lazy(() =>
  import('@tanstack/react-router-devtools').then((module) => ({
    default: module.TanStackRouterDevtools,
  })),
);

const ReactQueryDevtools = lazy(() =>
  import('@tanstack/react-query-devtools').then((module) => ({
    default: module.ReactQueryDevtools,
  })),
);

const DevTools = lazy(() =>
  import('@porsche-code/kado-devtools').then((module) => ({
    default: module.DevTools,
  })),
);

interface KadoRouterContext {
  breadcrumb?: (match: AnyRouteMatch) => React.ReactElement;
  pageTitle?: () => React.ReactElement;
}

export const Route = createRootRouteWithContext<KadoRouterContext>()({
  component: () => {
    const { t } = useTranslation(NAMESPACES);
    const { state: personState } = usePersonContext();
    const location = useLocation();

    useEffect(() => {
      Rum.recordPageView(location.pathname);
    }, [location]);

    return (
      <>
        <Page>
          <Header />
          {personState.isAuthenticated && <Divider />}
          <Main>
            <ContentWrapper>
              {personState.isAuthorized && (
                <Breadcrumbs
                  kadoName={t('common:title')}
                  appName={t('common:app.dashboard')}
                />
              )}

              <SignIn />
            </ContentWrapper>
          </Main>
          <Footer />
        </Page>

        {process.env.NODE_ENV === 'development' && (
          <Suspense fallback={<></>}>
            <ReactQueryDevtools buttonPosition="bottom-left" />
            <TanStackRouterDevtools
              toggleButtonProps={{ style: { left: '4rem', bottom: '1rem' } }}
            />
            <DevTools />
          </Suspense>
        )}
      </>
    );
  },
  notFoundComponent: () => (
    <PageNotAvailable
      text={<Trans i18nKey="pageNotAvailable" ns={COMMON_NAMESPACE} />}
    />
  ),
});
